import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Center,
  Skeleton,
  Text,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { AlertDialogModal } from 'clipsal-cortex-ui/src/components/AlertDialogModal';
import { useAppVisibility } from 'clipsal-cortex-utils/src/hooks/use-app-visibility';

import { ArcButton } from '../../../../../common/components/ArcButton';
import { SlidingAnimationPageBase } from '../../../../../common/components/SlidingAnimationPageBase';
import { useParamBackUrl } from '../../../../../common/hooks/use-param-back-url';
import { ACTIVE_DATA_EXPORT_STATUSES, DATA_EXPORT_POLLING_TIME_MS } from './constants';
import { useGetDataExportsQuery, useRequestDataExportMutation } from './dataExportApi';
import { DataExportListItem } from './DataExportListItem';
import { useDownloadDataExport } from './useDownloadDataExport';

export function DataArchive() {
  const { t } = useTranslation();
  const toast = useToast({ isClosable: true });
  const backURL = useParamBackUrl('../?direction=back');
  const isAppVisible = useAppVisibility();
  // gray.500 is too low-contrast against the dark SEHomeCard background; customGrey.300 keeps
  // muted text legible there while customGrey.700 matches the light-mode card contrast.
  const mutedTextColor = useColorModeValue('customGrey.700', 'customGrey.300');

  // Poll while a job is still building (and the app is in the foreground); stop once everything is
  // COMPLETED/FAILED. Derived from the query's own data via state so the interval updates as jobs settle.
  const [pollingInterval, setPollingInterval] = useState(0);
  const { data: exports = [], isLoading, isError, refetch } = useGetDataExportsQuery(undefined, { pollingInterval });
  const hasActiveExport = exports.some(({ status }) => ACTIVE_DATA_EXPORT_STATUSES.includes(status));

  useEffect(() => {
    if (isAppVisible && hasActiveExport) {
      setPollingInterval(DATA_EXPORT_POLLING_TIME_MS);
      // Re-arming the interval only schedules the *next* poll, so pull fresh status immediately —
      // otherwise a foreground resume shows stale "Building" state for up to a full interval.
      refetch();
    } else {
      setPollingInterval(0);
    }
  }, [isAppVisible, hasActiveExport, refetch]);

  const [requestDataExport, { isLoading: isRequesting }] = useRequestDataExportMutation();
  const { downloadExport, downloadingReferenceId, isReauthDialogOpen, confirmReauth, cancelReauth } =
    useDownloadDataExport(refetch);

  async function handleRequestExport() {
    try {
      await requestDataExport().unwrap();
    } catch {
      toast({
        title: t('Data Archive.error requesting'),
        description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
        status: 'error',
      });
    }
  }

  return (
    <SlidingAnimationPageBase
      px={4}
      title={t('Data Archive.data archive')}
      backURL={backURL}
      data-testid="data-archive"
    >
      <Box>
        <Text my={4}>{t('Data Archive.intro')}</Text>

        <ArcButton
          arcColor="#3DCD57"
          w="100%"
          py={6}
          isLoading={isRequesting}
          isDisabled={hasActiveExport}
          onClick={handleRequestExport}
          data-testid="request-data-export-button"
        >
          {t('Data Archive.request data export')}
        </ArcButton>

        <Box mt={6}>
          {isLoading ? (
            <VStack align="stretch" spacing={3} data-testid="data-archive-loading">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} height="96px" borderRadius="md" />
              ))}
            </VStack>
          ) : isError ? (
            <Alert status="error" borderRadius="md" data-testid="data-archive-error">
              <AlertIcon />
              <AlertDescription>
                {t('Common.please try again')} {t('Common.if this persists contact support')}
              </AlertDescription>
            </Alert>
          ) : exports.length ? (
            exports.map((dataExport) => (
              <DataExportListItem
                key={dataExport.reference_id}
                dataExport={dataExport}
                isDownloading={downloadingReferenceId === dataExport.reference_id}
                onDownload={downloadExport}
                onRetry={handleRequestExport}
              />
            ))
          ) : (
            <Center my={6}>
              <Text color={mutedTextColor} data-testid="data-archive-empty">
                {t('Data Archive.no exports')}
              </Text>
            </Center>
          )}
        </Box>
      </Box>

      <AlertDialogModal
        isOpen={isReauthDialogOpen}
        onClose={cancelReauth}
        onConfirm={confirmReauth}
        header={t('Data Archive.reauth confirm title')}
        subHeader={t('Data Archive.reauth confirm description')}
        confirmButtonName={t('Data Archive.reauth confirm button')}
        cancelButtonName={t('Common.cancel')}
        dialogContentTestId="reauth-confirm-dialog"
      />
    </SlidingAnimationPageBase>
  );
}
